import { createContext, useContext, useEffect, useState } from "react";
import {
  getMeetingSchedulerData,
  getMenteeDetails,
  getMenteeScore,
  getMentorMenteeMapping,
} from "../utils/services";
import { useUserContext } from "./UserContext";
import { PROGRAM_NAME, statusType } from "../constants/constants";
import { enqueueSnackbar } from "notistack";
import { currentQuater, getPreviousQuarter } from "../utils/validation";

const menteeContext = createContext();

export const MenteeContextProvider = ({ children }) => {
  // selected mentee
  const [mentee, setMentee] = useState({});
  // mentees list assigned to logged in mentor
  const [menteeList, setMenteeList] = useState([]);
  // schedule response received from backend
  const [scheduleList, setScheduleList] = useState([]);

  const [dashboardList, setDashboardList] = useState([]);

  const [scoreData, setScoreData] = useState([]);
  const [quarter, setQuarter] = useState({});

  // loading
  const [loading, setLoading] = useState(true);

  // logged in user
  const { user } = useUserContext();

  // filter the selected mentee using id and save in mentee variable
  const getSelectedMenteeData = (id) => {
    const mentees = menteeList.filter((x) => x.id == id);
    setMentee(mentees[0]);
  };

  // clear selected mentee
  const clearMenteeData = () => {
    setMentee({});
  };

  // get all previous quarters
  const getAllPreviousQuaterName = () => {
    let currentQuarterName = currentQuater();
    setQuarter((value) => ({
      ...value,
      currentQuarter: currentQuarterName,
    }));
    let firstPreviousQuarter = getPreviousQuarter(currentQuarterName);
    setQuarter((value) => ({
      ...value,
      firstPreviousQuarter: firstPreviousQuarter,
    }));
    let secondPreviousQuater = getPreviousQuarter(firstPreviousQuarter);
    setQuarter((value) => ({
      ...value,
      secondPreviousQuater: secondPreviousQuater,
    }));
    let thirdPreviousQuarter = getPreviousQuarter(secondPreviousQuater);
    setQuarter((value) => ({
      ...value,
      thirdPreviousQuarter: thirdPreviousQuarter,
    }));
    let fourthPreviousQuater = getPreviousQuarter(thirdPreviousQuarter);
    setQuarter((value) => ({
      ...value,
      fourthPreviousQuater: fourthPreviousQuater,
    }));
  };

  // Combining mentee list and dashboard list data together.....
  const combinedScoreData = () => {
    const scoreList = menteeList.map((data) => {
      let obj = {
        name: data.name,
        id: data.id,
        programId: data.programId,
        programName: data.programName,
        primaryDomain: data.primaryDomain,
        specialization: data.specialization,
      };
      for (const object of dashboardList) {
        if (object.menteeId === data.id) {
          if (quarter?.currentQuarter == object.quaterName) {
            obj["currentQuarter"] = object;
          }
          if (quarter?.firstPreviousQuarter == object.quaterName) {
            obj["firstPreviousQuarter"] = object;
          }
          if (quarter?.secondPreviousQuater == object.quaterName) {
            obj["secondPreviousQuater"] = object;
          }
          if (quarter?.thirdPreviousQuarter == object.quaterName) {
            obj["thirdPreviousQuarter"] = object;
          }
          if (quarter?.fourthPreviousQuater == object.quaterName) {
            obj["fourthPreviousQuater"] = object;
          }
        }
      }
      return obj;
    });

    setScoreData(scoreList);
    setLoading(false);
  };

  const getDashboardData = async (menteeIdList) => {
    const result = await getMenteeScore(user.token, menteeIdList);
    if (result.status == 200) {
      setDashboardList(result.data);
    } else {
      setDashboardList([]);
      enqueueSnackbar(
        "Something went wrong while fetch dashboard data. Please try again later.",
        {
          variant: "error",
        }
      );
    }
  };

  const getMenteeMappingAndData = async () => {
    setLoading(true);
    // get mentee assigned to logged in mentor
    const response = await getMentorMenteeMapping(user.token, user.id);
    if (response.status == 200) {
      // get list of id of mentees in format [{id : 123456}]
      const menteeIdList = response.data?.map((x) => ({ id: x.menteeId }));
      const programIds = {};

      response.data.forEach((element) => {
        programIds[element.menteeId] = element.programId;
      });

      // get mentee details
      const res = await getMenteeDetails(user.token, menteeIdList);

      if (res.status == "success" && JSON.parse(res.data)) {
        const data = JSON.parse(res.data);

        setMenteeList(
          data.map((x, i) => {
            return {
              id: x.id,
              name: `${x.firstName} ${x.lastName}`,
              email: x.emailAddress,
              phone: x.primaryContactNumber,
              programId: programIds[x.id],
              programName: PROGRAM_NAME[programIds[x.id]],
              scheduleDate: "",
              scheduleType: "",
              status: statusType.Pending,
              scheduleId: "",
              primaryDomain: x.primaryDomain,
              specialization: x.specialization,
            };
          })
        );
        enqueueSnackbar(`${data.length} facilitators are assigned to you.`, {
          variant: "success",
        });

        // get schedules record if exists
        getMenteeSchedules();

        // get the dashboard data
        getDashboardData(menteeIdList);
      } else {
        setMenteeList([]);
        setScheduleList([]);
        enqueueSnackbar("There were no facilitators assigned to you.", {
          variant: "info",
        });
      }
    } else {
      enqueueSnackbar(
        "Something went wrong in fetching facilitators assigned to you. Please try again later.",
        {
          variant: "error",
        }
      );
    }
  };

  // get current querter schedule data
  const getMenteeSchedules = async () => {
    setLoading(true);
    const response = await getMeetingSchedulerData(user.id, user.token);

    if (response.status == 200) {
      setScheduleList(response.data);
      setMenteeList((oldMentee) => {
        return oldMentee.map((mentee) => {
          const schedule = response.data.filter((x) => x.menteeId == mentee.id);
          if (schedule.length == 0) {
            return mentee;
          } else {
            mentee.primaryDomain =
              schedule[0].primaryDomain || mentee.primaryDomain;
            mentee.specialization =
              schedule[0].specialization || mentee.specialization;
            mentee.scheduleType = schedule[0].scheduleFor;
            mentee.scheduleDate = schedule[0].scheduleDate;
            mentee.status = schedule[0].sessionStatus;
            mentee.scheduleId = schedule[0].id;
            return mentee;
          }
        });
      });
    } else {
      setMenteeList([]);
      setScheduleList([]);
      enqueueSnackbar(
        "Something went wrong while fetching schedule data. Please try again later.",
        { variant: "error" }
      );
    }
    setLoading(false);
  };

  const updateDashboardData = () => {
    const menteeIdList = menteeList.map((x) => ({ id: x.id }));
    getDashboardData(menteeIdList);
  };

  useEffect(() => {
    if (user.id) {
      getMenteeMappingAndData();
    }
  }, [user]);

  useEffect(() => {
    getAllPreviousQuaterName();
  }, []);

  useEffect(() => {
    combinedScoreData();
  }, [dashboardList]);

  return (
    <menteeContext.Provider
      value={{
        mentee,
        menteeList,
        scheduleList,
        getSelectedMenteeData,
        getMenteeSchedules,
        clearMenteeData,
        dashboardList,
        setDashboardList,
        scoreData,
        quarter,
        loading,
        updateDashboardData,
        getMenteeMappingAndData,
      }}
    >
      {children}
    </menteeContext.Provider>
  );
};

export const useMenteeContext = () => {
  return useContext(menteeContext);
};
